import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useId, useRef, useState } from "react";

import type { CityLocation } from "../domain/types";

interface CitySearchProps {
  onSearch: (query: string) => Promise<CityLocation[]>;
  onSelect: (city: CityLocation) => void;
  selectedCity: CityLocation | null;
  compact?: boolean;
}

export function CitySearch({ onSearch, onSelect, selectedCity, compact = false }: CitySearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CityLocation[]>([]);
  const [resultsFor, setResultsFor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const requestSequence = useRef(0);
  const listboxId = useId();

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < 2) return;

    const sequence = ++requestSequence.current;
    const timer = window.setTimeout(() => {
      setIsLoading(true);
      void onSearch(normalized)
        .then((nextResults) => {
          if (sequence === requestSequence.current) {
            setResults(nextResults);
            setResultsFor(normalized);
          }
        })
        .catch(() => {
          if (sequence === requestSequence.current) {
            setResults([]);
            setResultsFor(normalized);
          }
        })
        .finally(() => {
          if (sequence === requestSequence.current) setIsLoading(false);
        });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [onSearch, query]);

  const chooseCity = (city: CityLocation) => {
    onSelect(city);
    setQuery(city.name);
    setResults([]);
  };
  const hasCurrentResults = query.trim().length >= 2 && resultsFor === query.trim() && results.length > 0;

  return (
    <div className={`city-search${compact ? " city-search--compact" : ""}`}>
      <SearchOutlined aria-hidden className="city-search__icon" />
      <input
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={hasCurrentResults}
        aria-label="搜索城市"
        className="city-search__input"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && hasCurrentResults) {
            event.preventDefault();
            chooseCity(results[0]);
          }
        }}
        placeholder={compact ? "搜索城市" : "搜索城市，例如上海、宁波"}
        role="combobox"
        value={query}
      />
      {isLoading && query.trim().length >= 2 ? <span className="city-search__loading">搜索中</span> : null}
      {hasCurrentResults ? (
        <ul className="city-search__results glass-panel" id={listboxId} role="listbox">
          {results.map((city) => (
            <li key={city.id}>
              <button
                aria-selected={selectedCity?.id === city.id}
                className="city-search__result"
                onClick={() => chooseCity(city)}
                role="option"
                type="button"
              >
                <strong>{city.name}</strong>
                <span>{city.adm1} · {city.adm2}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
